import { useState, useEffect } from "react";
import { API, useAuth, authFetch } from "@/App";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, AlertTriangle, DollarSign, Calendar,
  ChevronDown, ChevronUp, Phone, MapPin, Settings, Save, BarChart3,
  ArrowUpRight, ArrowDownRight, Shield, Users, Clock, Package, CalendarRange,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const formatINR = (paise) => {
  if (!paise) return "\u20B90";
  const r = paise / 100;
  if (r >= 10000000) return `\u20B9${(r / 10000000).toFixed(1)}Cr`;
  if (r >= 100000) return `\u20B9${(r / 100000).toFixed(1)}L`;
  if (r >= 1000) return `\u20B9${(r / 1000).toFixed(1)}K`;
  return `\u20B9${r.toFixed(0)}`;
};

const SeverityBadge = ({ severity }) => {
  const s = { HIGH: "bg-red-100 text-red-700", MEDIUM: "bg-amber-100 text-amber-700", LOW: "bg-yellow-100 text-yellow-700" };
  return <Badge className={`${s[severity] || "bg-slate-100"} text-[10px]`}>{severity}</Badge>;
};

const SalesForecastPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regionFilter, setRegionFilter] = useState("ALL");
  const [threshold, setThreshold] = useState(21);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [warningFilter, setWarningFilter] = useState("all");

  useEffect(() => { fetchData(); fetchThreshold(); }, [regionFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const url = regionFilter !== "ALL" ? `${API}/sales-forecast?region=${regionFilter}` : `${API}/sales-forecast`;
      const res = await authFetch(url);
      const d = await res.json();
      if (d.success) setData(d.data);
    } catch { toast.error("Failed to load forecast"); }
    finally { setLoading(false); }
  };

  const fetchThreshold = async () => {
    try {
      const res = await authFetch(`${API}/settings/payment-delay`);
      const d = await res.json();
      if (d.success) setThreshold(d.data.days);
    } catch {}
  };

  const saveThreshold = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/settings/payment-delay`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ days: threshold }),
      });
      const d = await res.json();
      if (d.success) { toast.success(`Threshold updated to ${threshold} days`); setSettingsOpen(false); fetchData(); }
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const warnings = data?.payment_warnings?.filter(w => warningFilter === "all" || w.severity === warningFilter) || [];

  if (loading) return (
    <div className="space-y-6">
      <div className="h-10 bg-slate-100 rounded animate-pulse" />
      <div className="grid grid-cols-3 gap-4">{[1,2,3].map(i => <div key={i} className="h-32 bg-slate-100 rounded-xl animate-pulse" />)}</div>
      <div className="h-64 bg-slate-100 rounded-xl animate-pulse" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="forecast-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Sales Forecast & Analytics</h1>
          <p className="text-slate-500 text-sm mt-1">AI-powered predictions and payment delay monitoring</p>
        </div>
        <div className="flex gap-2">
          <Select value={regionFilter} onValueChange={setRegionFilter}>
            <SelectTrigger className="w-[140px]" data-testid="forecast-region"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Regions</SelectItem>
              <SelectItem value="GJ">Gujarat</SelectItem>
              <SelectItem value="RJ">Rajasthan</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="btn-delay-settings">
            <Settings className="w-4 h-4 mr-1" /> Delay: {data?.payment_delay_threshold || 21}d
          </Button>
        </div>
      </div>

      {/* Region Forecast Cards */}
      {data?.region_forecast && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(data.region_forecast).map(([rid, rf]) => (
            <Card key={rid} className="p-5 border-l-4" style={{ borderLeftColor: rid === "GJ" ? "#C0272D" : "#F5A623" }} data-testid={`region-forecast-${rid}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">{rid === "GJ" ? "Gujarat" : "Rajasthan"}</h3>
                <Badge style={{ backgroundColor: `${rid === "GJ" ? "#C0272D" : "#F5A623"}20`, color: rid === "GJ" ? "#C0272D" : "#F5A623" }}>
                  {rf.predicted_growth > 0 ? "+" : ""}{rf.predicted_growth}% Growth
                </Badge>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><p className="text-xs text-slate-400">Current Revenue</p><p className="text-lg font-bold font-mono">{formatINR(rf.current_revenue)}</p></div>
                <div><p className="text-xs text-slate-400">Predicted Growth</p>
                  <p className={`text-lg font-bold font-mono ${rf.predicted_growth >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {rf.predicted_growth >= 0 ? <ArrowUpRight className="w-4 h-4 inline" /> : <ArrowDownRight className="w-4 h-4 inline" />}
                    {rf.predicted_growth}%
                  </p>
                </div>
                <div><p className="text-xs text-slate-400">Target Achievement</p>
                  <p className="text-lg font-bold font-mono">{rf.target_achievement}%</p>
                  <div className="w-full h-1.5 bg-slate-200 rounded-full mt-1">
                    <div className="h-full rounded-full" style={{ width: `${rf.target_achievement}%`, backgroundColor: rf.target_achievement >= 80 ? "#10B981" : rf.target_achievement >= 60 ? "#F59E0B" : "#EF4444" }} />
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Revenue Forecast Chart (Simple bar representation) */}
      <Card className="p-6" data-testid="forecast-chart">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#8B1A1A]" /> Revenue Trend & Forecast</h3>
        <div className="flex items-end gap-1 h-48">
          {data?.historical?.map((h, i) => {
            const maxRev = Math.max(...(data.historical || []).map(x => x.revenue), ...(data.forecast || []).map(x => x.revenue_mid));
            const pct = (h.revenue / maxRev) * 100;
            return (
              <div key={`h-${i}`} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[9px] font-mono text-slate-500">{formatINR(h.revenue)}</span>
                <div className="w-full bg-blue-400 rounded-t transition-all" style={{ height: `${pct}%` }} />
                <span className="text-[10px] text-slate-600 font-medium">{h.month}</span>
              </div>
            );
          })}
          <div className="w-px h-full bg-slate-300 mx-1" />
          {data?.forecast?.map((f, i) => {
            const maxRev = Math.max(...(data.historical || []).map(x => x.revenue), ...(data.forecast || []).map(x => x.revenue_mid));
            const pctMid = (f.revenue_mid / maxRev) * 100;
            return (
              <div key={`f-${i}`} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[9px] font-mono text-emerald-600">{formatINR(f.revenue_mid)}</span>
                <div className="w-full rounded-t relative" style={{ height: `${pctMid}%` }}>
                  <div className="absolute inset-0 bg-emerald-200 rounded-t opacity-50" />
                  <div className="absolute inset-x-0 bottom-0 bg-emerald-500 rounded-t" style={{ height: "70%" }} />
                </div>
                <span className="text-[10px] text-emerald-700 font-medium">{f.month}</span>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-4 mt-3 text-xs">
          <div className="flex items-center gap-1"><div className="w-3 h-3 bg-blue-400 rounded" /> Historical</div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 bg-emerald-500 rounded" /> Predicted</div>
        </div>
      </Card>

      {/* Top & Bottom Dealers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-5" data-testid="top-dealers">          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-600" /> Top Performing Dealers</h3>
          <div className="space-y-2">
            {data?.top_dealers?.map((d, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-emerald-50/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold">{i+1}</span>
                  <div>
                    <p className="text-sm font-medium text-slate-900">{d.name}</p>
                    <p className="text-xs text-slate-500">{d.district}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold font-mono">{formatINR(d.revenue)}</p>
                  <p className="text-xs text-emerald-600">+{d.growth}%</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-5" data-testid="underperforming-dealers">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><TrendingDown className="w-4 h-4 text-red-500" /> Underperforming Dealers</h3>
          <div className="space-y-2">
            {data?.underperforming?.map((d, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-red-50/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center text-xs font-bold">{i+1}</span>
                  <div>
                    <p className="text-sm font-medium text-slate-900">{d.name}</p>
                    <p className="text-xs text-slate-500">{d.district}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold font-mono">{formatINR(d.revenue)}</p>
                  <p className="text-xs text-red-600">{d.decline}%</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Top Products + Top Months (P2 iter81 deep-dive) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-5" data-testid="top-products">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Package className="w-4 h-4 text-violet-600" /> Top Performing Products
            <Badge variant="outline" className="ml-auto text-[10px]">By Revenue</Badge>
          </h3>
          {(!data?.top_products || data.top_products.length === 0) ? (
            <p className="text-xs text-slate-400 italic text-center py-8">No order data yet — top products will appear once dealers start placing orders.</p>
          ) : (
            <div className="space-y-2">
              {data.top_products.map((p, i) => {
                const max = Math.max(...data.top_products.map((x) => x.revenue), 1);
                const pct = Math.round((p.revenue / max) * 100);
                return (
                  <div key={p.product_id} className="p-2.5 bg-violet-50/40 rounded-lg" data-testid={`top-product-${i}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-5 h-5 rounded-full bg-violet-600 text-white flex items-center justify-center text-[10px] font-bold shrink-0">{i + 1}</span>
                        <p className="text-sm font-medium text-slate-900 truncate" title={p.product_name}>{p.product_name}</p>
                      </div>
                      <p className="text-sm font-bold font-mono shrink-0">{formatINR(p.revenue)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-violet-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <p className="text-[10px] text-slate-500 font-mono shrink-0">{p.units_sold} units · {p.order_count} orders</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
        <Card className="p-5" data-testid="top-months">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <CalendarRange className="w-4 h-4 text-blue-600" /> Best Performing Months
            <Badge variant="outline" className="ml-auto text-[10px]">All Time</Badge>
          </h3>
          {(!data?.top_months || data.top_months.length === 0) ? (
            <p className="text-xs text-slate-400 italic text-center py-8">No order data yet — best months will appear after first orders are recorded.</p>
          ) : (
            <div className="space-y-2">
              {data.top_months.slice(0, 6).map((m, i) => {
                const max = Math.max(...data.top_months.map((x) => x.revenue), 1);
                const pct = Math.round((m.revenue / max) * 100);
                let label = m.month;
                try {
                  const [y, mo] = m.month.split("-");
                  label = new Date(parseInt(y), parseInt(mo) - 1).toLocaleString("en-IN", { month: "short", year: "numeric" });
                } catch { /* keep raw */ }
                return (
                  <div key={m.month} className="p-2.5 bg-blue-50/40 rounded-lg" data-testid={`top-month-${i}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] font-bold shrink-0">{i + 1}</span>
                        <p className="text-sm font-medium text-slate-900">{label}</p>
                      </div>
                      <p className="text-sm font-bold font-mono shrink-0">{formatINR(m.revenue)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <p className="text-[10px] text-slate-500 font-mono shrink-0">{m.orders} orders</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Payment Delay Warnings */}
      <Card className="p-5" data-testid="payment-warnings">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" /> Payment Delay Warnings
            {warnings.length > 0 && <Badge className="bg-red-100 text-red-700">{warnings.length}</Badge>}
          </h3>
          <div className="flex gap-2">
            <Select value={warningFilter} onValueChange={setWarningFilter}>
              <SelectTrigger className="w-[120px] h-8 text-xs" data-testid="warning-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                <SelectItem value="HIGH">High</SelectItem>
                <SelectItem value="MEDIUM">Medium</SelectItem>
                <SelectItem value="LOW">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {warnings.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <Shield className="w-8 h-8 mx-auto mb-2 text-emerald-300" />
            <p className="text-sm">No payment delays detected (threshold: {data?.payment_delay_threshold} days)</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="warnings-table">
              <thead><tr><th>Dealer</th><th>District</th><th>Region</th><th>Last Order</th><th>Amount</th><th>Days Overdue</th><th>Severity</th><th>Contact</th></tr></thead>
              <tbody>
                {warnings.map((w, i) => (
                  <tr key={i}>
                    <td><span className="font-medium">{w.firm_name}</span><br/><span className="text-xs text-slate-500">{w.contact_person}</span></td>
                    <td>{w.district}</td>
                    <td>{w.region_id === "GJ" ? <Badge className="bg-[#C0272D]/10 text-[#C0272D] text-[10px]">GJ</Badge> : <Badge className="bg-[#F5A623]/10 text-[#F5A623] text-[10px]">RJ</Badge>}</td>
                    <td className="text-xs">{new Date(w.last_order_date).toLocaleDateString("en-IN")}</td>
                    <td className="font-mono">{formatINR(w.order_amount)}</td>
                    <td><span className="font-bold text-red-600">{w.days_overdue}d</span></td>
                    <td><SeverityBadge severity={w.severity} /></td>
                    <td>{w.mobile_number && <a href={`tel:${w.mobile_number}`} className="text-blue-600 hover:underline text-xs flex items-center gap-1"><Phone className="w-3 h-3" />{w.mobile_number}</a>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Payment Delay Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-sm" data-testid="delay-settings-dialog">
          <DialogHeader><DialogTitle>Payment Delay Settings</DialogTitle></DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">Payment Delay Threshold (Days)</label>
              <Input type="number" value={threshold} onChange={(e) => setThreshold(parseInt(e.target.value) || 21)}
                min={1} max={365} data-testid="input-threshold" />
              <p className="text-xs text-slate-500 mt-1">Dealers with no payment after this many days since last order will be flagged.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={saveThreshold} disabled={saving} data-testid="save-threshold">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesForecastPage;
